import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "./constant_api";
import { useSearchParams } from "react-router-dom";

const SelectBankAccountModal = ({ isOpen, organisationId, onClose, onSubmit }) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const OrganisationID = searchParams.get("OrganisationID") || "";


  useEffect(() => {
    if (isOpen && OrganisationID) {
      fetchBankAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, OrganisationID]);

  const fetchBankAccounts = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${BASE_URL}/organisation/get-bank-accounts`,
        {
          params: { OrganisationID: OrganisationID },
        }
      );

      if (response?.data?.success) {
        setAccounts(response.data.data || []);
      } else {
        setError(response?.data?.message || "Failed to fetch bank accounts");
      }
    } catch (err) {
      console.error("Error fetching bank accounts:", err);
      setError(
        err?.response?.data?.message || "Something went wrong while fetching accounts"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (accountId) => {
    setSelectedIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === accounts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(accounts.map((acc) => acc.AccountID));
    }
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      setError("Please select at least one account");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      // 1. Sync selected accounts
      const syncResponse = await axios.post(
        `${BASE_URL}/organisation/sync-new-data`,
        {
          OrganisationID: OrganisationID,
          Accounts: selectedIds,
        }
      );

      if (syncResponse?.data?.success === false) {
        setError(
          syncResponse?.data?.message || "Failed to sync selected accounts"
        );
        return;
      }

      // 2. Remove selected accounts after sync success
      const removeResponse = await axios.post(
        `${BASE_URL}/organisation/remove-selected-accounts`,
        {
          OrganisationID: organisationId,
          Accounts: selectedIds,
        }
      );

      if (removeResponse?.data?.success === false) {
        setError(
          removeResponse?.data?.message ||
          "Accounts synced, but failed to remove selected accounts"
        );
        return;
      }

      // 3. Close modal
      onClose?.();

      // 4. Redirect after both APIs succeed
      window.location.href = `/query`;
    } catch (err) {
      console.error("Error submitting selected accounts:", err);

      setError(
        err?.response?.data?.message ||
        "Failed to submit selected accounts"
      );
    } finally {
      setSubmitting(false);
    }
  };
  const handleClose = () => {
    setSelectedIds([]);
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="sba-modal-overlay">
      <div className="sba-modal">
        <div className="sba-modal-header">
          <h4>Select Bank Accounts</h4>
          <button
            type="button"
            className="sba-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="sba-modal-body">
          {loading && (
            <div className="sba-loading">
              <div className="sk-spinner sk-spinner-pulse"></div>
              <span>Loading accounts...</span>
            </div>
          )}

          {!loading && error && <div className="sba-error">{error}</div>}

          {!loading && !error && accounts.length === 0 && (
            <div className="sba-empty">No bank accounts found.</div>
          )}

          {!loading && accounts.length > 0 && (
            <>
              <div className="sba-select-all-row">
                <label className="sba-checkbox-label">
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.length === accounts.length &&
                      accounts.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                  <span>Select All</span>
                </label>
              </div>

              <div className="sba-account-list">
                {accounts.map((account) => (
                  <div className="sba-account-row" key={account.AccountID}>
                    <label className="sba-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(account.AccountID)}
                        onChange={() => handleCheckboxChange(account.AccountID)}
                      />
                      <div className="sba-account-info">
                        <span className="sba-account-name">
                          {account.Name}
                        </span>
                        <span className="sba-account-meta">
                          {account.BankAccountType} &middot; {account.CurrencyCode}
                          {account.BankAccountNumber
                            ? ` \u00b7 ****${account.BankAccountNumber.slice(-4)}`
                            : ""}
                        </span>
                      </div>
                      <span
                        className={`sba-status-badge sba-status-${(
                          account.Status || ""
                        ).toLowerCase()}`}
                      >
                        {account.Status}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="sba-modal-footer">
          <span className="sba-selected-count">
            {selectedIds.length} selected
          </span>
          <div className="sba-footer-actions">
            <button
              type="button"
              className="sba-btn sba-btn-secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sba-btn sba-btn-primary"
              onClick={handleSubmit}
              disabled={submitting || loading || selectedIds.length === 0}
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectBankAccountModal;